(() => {
  const tagName = 'pdp-bundle-selector';
  const scrollLockClasses = ['modal-show', 'modal-showing', 'body-no-scrollbar'];

  const unlockPageScroll = () => {
    document.body.classList.remove(...scrollLockClasses);
    document.body.style.removeProperty('padding-right');
  };

  const hasOpenLockingSurface = () =>
    !!document.querySelector(
      [
        'cart-drawer[open]',
        'cart-drawer[active]',
        'drawer-component[open]',
        'drawer-component[active]',
        'modal-component[open]',
        'modal-component[active]',
        'basic-modal[open]',
        'basic-modal[active]',
        'search-drawer[open]',
        'search-drawer[active]',
        'menu-drawer[open]',
        'menu-drawer[active]',
        'quick-view-modal[open]',
        'quick-view-modal[active]',
        'subscription-popup[open]',
        'subscription-popup[active]',
        'pickup-availability-drawer[open]',
        'pickup-availability-drawer[active]',
      ].join(',')
    );

  const cleanupStaleCartDrawerScrollLock = () => {
    [450, 900].forEach((delay) => {
      window.setTimeout(() => {
        if (hasOpenLockingSurface()) return;
        unlockPageScroll();
      }, delay);
    });
  };

  const closeRestoredCartDrawer = () => {
    let closedDrawer = false;

    document.querySelectorAll('cart-drawer[open], cart-drawer[active]').forEach((drawer) => {
      closedDrawer = true;
      drawer.removeAttribute('open');
      drawer.removeAttribute('active');
      drawer.setAttribute('inert', '');
      drawer.hidden = true;

      drawer.controls?.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    });

    if (!closedDrawer) return;

    unlockPageScroll();
  };

  const restoreBundleSelectors = () => {
    const selectors = document.querySelectorAll(tagName);
    if (!selectors.length) return;

    selectors.forEach((selector) => {
      if (typeof selector.restoreInteractiveState === 'function') {
        selector.restoreInteractiveState();
        return;
      }

      const submitButton = selector.querySelector('[data-bundle-submit]');
      if (submitButton) {
        submitButton.classList.remove('btn--loading');
        submitButton.disabled = false;
      }
    });

    closeRestoredCartDrawer();
  };

  const hasRenderableSections = (sections, sectionIds) =>
    !!sections &&
    sectionIds.length > 0 &&
    sectionIds.every((sectionId) => typeof sections[sectionId] === 'string' && sections[sectionId].trim() !== '');

  if (!window.__duoriPdpBundleRestoreBound) {
    window.addEventListener('pagehide', restoreBundleSelectors);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) restoreBundleSelectors();
    });
    window.__duoriPdpBundleRestoreBound = true;
  }

  if (!window.__duoriPdpBundleCartUnlockBound) {
    document.addEventListener('drawer:handleAfterHide', cleanupStaleCartDrawerScrollLock);
    document.addEventListener('modal:handleAfterHide', cleanupStaleCartDrawerScrollLock);
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[aria-controls="CartDrawer"]')) return;
      cleanupStaleCartDrawerScrollLock();
    });
    document.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape') return;
      cleanupStaleCartDrawerScrollLock();
    });
    window.__duoriPdpBundleCartUnlockBound = true;
  }

  if (customElements.get(tagName)) return;

class PdpBundleSelector extends HTMLElement {
  constructor() {
    super();

    this.requiredCount = 3;
    this.paidCount = 2;
    this.freeCount = 1;
    this.purchaseType = 'one_time';
    this.units = [];
    this.bundleMemoryUnits = [];
    this.slots = [];
    this.activeSlotIndex = null;
    this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
    this.offerButtons = Array.from(this.querySelectorAll('[data-bundle-offer]'));
    this.purchaseButtons = Array.from(this.querySelectorAll('[data-purchase-type]'));
    this.singleDailyElement = this.querySelector('[data-bundle-single-daily]');
    this.builderElement = this.querySelector('[data-bundle-builder]');
    this.slotContainer = this.querySelector('[data-bundle-slots]');
    this.pickerElement = this.querySelector('[data-bundle-picker]');
    this.pickerGrid = this.querySelector('[data-bundle-picker-grid]');
    this.pickerTitleElement = this.querySelector('[data-bundle-picker-title]');
    this.pickerCloseButton = this.querySelector('[data-bundle-picker-close]');
    this.progressElement = this.querySelector('[data-bundle-progress]');
    this.progressLabelElement = this.querySelector('[data-bundle-progress-label]');
    this.builderHintElement = this.querySelector('[data-bundle-builder-hint]');
    this.submitButton = this.querySelector('[data-bundle-submit]');
    this.submitText = this.querySelector('[data-bundle-submit-text]');
    this.countElement = this.querySelector('[data-bundle-count]');
    this.totalElement = this.querySelector('[data-bundle-total]');
    this.compareElement = this.querySelector('[data-bundle-compare]');
    this.saveElement = this.querySelector('[data-bundle-save]');
    this.oneTimeTotalElement = this.querySelector('[data-bundle-one-time-total]');
    this.oneTimeCompareElement = this.querySelector('[data-bundle-one-time-compare]');
    this.oneTimeSaveElement = this.querySelector('[data-bundle-one-time-save]');
    this.subscribeTotalElement = this.querySelector('[data-bundle-subscribe-total]');
    this.subscribeCompareElement = this.querySelector('[data-bundle-subscribe-compare]');
    this.subscribeSaveElement = this.querySelector('[data-bundle-subscribe-save]');
    this.subscribeDealBadgeElement = this.querySelector('[data-bundle-subscribe-deal-badge]');
    this.errorElement = this.querySelector('[data-bundle-error]');
    this.cartDrawerElement = document.querySelector('cart-drawer');

    this.handleOfferClick = this.handleOfferClick.bind(this);
    this.handlePurchaseClick = this.handlePurchaseClick.bind(this);
    this.handleCardClick = this.handleCardClick.bind(this);
    this.handleSlotClick = this.handleSlotClick.bind(this);
    this.handleSlotKeydown = this.handleSlotKeydown.bind(this);
    this.handlePickerClick = this.handlePickerClick.bind(this);
    this.handlePickerClose = this.handlePickerClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    const activeOfferButton = this.offerButtons.find((button) => button.classList.contains('is-active'));
    if (activeOfferButton) {
      this.requiredCount = Number(activeOfferButton.dataset.requiredCount || 1);
      this.freeCount = Number(activeOfferButton.dataset.freeCount || 0);
      this.paidCount = Number(
        activeOfferButton.dataset.paidCount || Math.max(this.requiredCount - this.freeCount, 1)
      );
    }

    const activePurchaseButton = this.purchaseButtons.find((button) => button.classList.contains('is-active'));
    if (activePurchaseButton) this.purchaseType = activePurchaseButton.dataset.purchaseType || 'one_time';

    this.offerButtons.forEach((button) => button.addEventListener('click', this.handleOfferClick));
    this.purchaseButtons.forEach((button) => button.addEventListener('click', this.handlePurchaseClick));
    this.cards.forEach((card) => card.addEventListener('click', this.handleCardClick));
    this.slotContainer && this.slotContainer.addEventListener('click', this.handleSlotClick);
    this.slotContainer && this.slotContainer.addEventListener('keydown', this.handleSlotKeydown);
    this.pickerGrid && this.pickerGrid.addEventListener('click', this.handlePickerClick);
    this.pickerCloseButton && this.pickerCloseButton.addEventListener('click', this.handlePickerClose);
    this.submitButton && this.submitButton.addEventListener('click', this.handleSubmit);
    document.addEventListener('variant:changed', this.handleVariantChange);
    this.rebuildSlots([], this.requiredCount > 1);
    this.renderPicker();
    this.render();
  }

  disconnectedCallback() {
    this.offerButtons.forEach((button) => button.removeEventListener('click', this.handleOfferClick));
    this.purchaseButtons.forEach((button) => button.removeEventListener('click', this.handlePurchaseClick));
    this.cards.forEach((card) => card.removeEventListener('click', this.handleCardClick));
    this.slotContainer && this.slotContainer.removeEventListener('click', this.handleSlotClick);
    this.slotContainer && this.slotContainer.removeEventListener('keydown', this.handleSlotKeydown);
    this.pickerGrid && this.pickerGrid.removeEventListener('click', this.handlePickerClick);
    this.pickerCloseButton && this.pickerCloseButton.removeEventListener('click', this.handlePickerClose);
    this.submitButton && this.submitButton.removeEventListener('click', this.handleSubmit);
    document.removeEventListener('variant:changed', this.handleVariantChange);
  }

  formatMoney(cents) {
    if (window.FoxTheme?.Currency && FoxTheme.settings?.moneyFormat) {
      return FoxTheme.Currency.formatMoney(cents || 0, FoxTheme.settings.moneyFormat);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format((cents || 0) / 100);
  }

  getCardData(card) {
    return {
      id: String(card.dataset.variantId || ''),
      title: card.dataset.title || '',
      shortTitle: card.dataset.shortTitle || card.dataset.title || '',
      handle: card.dataset.handle || '',
      color: card.dataset.color || this.getProductColor(card.dataset.handle || '', card.dataset.title || ''),
      image: card.dataset.image || '',
      price: Number(card.dataset.price || 0),
      comparePrice: Number(card.dataset.comparePrice || card.dataset.price || 0),
      subPrice: Number(card.dataset.subPrice || 0),
      subComparePrice: Number(card.dataset.subComparePrice || 0),
      sellingPlanId: card.dataset.sellingPlan || '',
      available: card.dataset.available === 'true',
    };
  }

  getCurrentVariantUnit() {
    const matchingCard = this.getMatchingCurrentCardUnit();
    if (matchingCard) return matchingCard;

    return {
      id: String(this.dataset.currentVariantId || ''),
      title: this.dataset.currentTitle || 'Single pack',
      shortTitle: this.getShortTitle(this.dataset.currentShortTitle || this.dataset.currentTitle || 'Single pack'),
      handle: this.dataset.currentHandle || '',
      color: this.getProductColor(this.dataset.currentHandle || '', this.dataset.currentTitle || ''),
      image: this.dataset.currentImage || '',
      price: Number(this.dataset.currentPrice || 0),
      comparePrice: Number(this.dataset.currentComparePrice || this.dataset.currentPrice || 0),
      subPrice: Number(this.dataset.currentSubPrice || 0),
      subComparePrice: Number(this.dataset.currentSubComparePrice || 0),
      sellingPlanId: this.dataset.currentSellingPlan || '',
      available: this.dataset.currentAvailable === 'true',
    };
  }

  getUnitPrice(unit) {
    if (this.purchaseType === 'subscribe' && unit.subPrice) return unit.subPrice;
    return unit.price;
  }

  getUnitComparePrice(unit) {
    if (this.purchaseType === 'subscribe' && unit.subComparePrice) return unit.subComparePrice;
    return unit.comparePrice || unit.price;
  }

  getProductColor(handle = '', title = '') {
    const key = `${handle} ${title}`.toLowerCase();
    if (key.includes('glp')) return '#7B4F9E';
    if (key.includes('neuro')) return '#2E7D9A';
    if (key.includes('sleep-plus') || key.includes('sleep plus') || key.includes('sleep+')) return '#1A3A7A';
    if (key.includes('sleep')) return '#3B5BA5';
    if (key.includes('collagen')) return '#7AAB3A';
    if (key.includes('unbloat')) return '#2BA8A8';
    if (key.includes('happy')) return '#E06B2E';
    if (key.includes('omega')) return '#E0A020';
    if (key.includes('flora')) return '#D45090';
    if (key.includes('sunshine')) return '#E8C030';
    if (key.includes('nad')) return '#8A8AC0';
    return '#8A8AC0';
  }

  getShortTitle(title = '') {
    const cleaned = String(title)
      .replace(/duori/gi, '')
      .replace(/patches/gi, '')
      .replace(/patch/gi, '')
      .trim();

    return cleaned || String(title || '').trim() || 'Patch';
  }

  getMatchingCurrentCardUnit() {
    const currentVariantId = String(this.dataset.currentVariantId || '');
    const currentHandle = this.dataset.currentHandle || '';
    const matchingCard = this.cards.find((card) => {
      if (currentVariantId && String(card.dataset.variantId || '') === currentVariantId) return true;
      return currentHandle && card.dataset.handle === currentHandle;
    });

    return matchingCard ? this.getCardData(matchingCard) : null;
  }

  getDealUnit(purchaseType = 'one_time') {
    const currentUnit = this.getCurrentVariantUnit();
    const unitPrice = purchaseType === 'subscribe' && currentUnit.subPrice ? currentUnit.subPrice : currentUnit.price;
    const comparePrice =
      purchaseType === 'subscribe' && currentUnit.subComparePrice
        ? currentUnit.subComparePrice
        : currentUnit.comparePrice || currentUnit.price;

    return { unitPrice, comparePrice };
  }

  getDealTotals(purchaseType = this.purchaseType) {
    if (purchaseType === 'subscribe') {
      const currentUnit = this.getCurrentVariantUnit();
      const regularUnitPrice = Math.max(currentUnit.price || currentUnit.subComparePrice || 0, 0);
      const subscriptionUnitPrice = Math.max(currentUnit.subPrice || regularUnitPrice, 0);
      const subscriptionDiscountPerPack = Math.max(regularUnitPrice - subscriptionUnitPrice, 0);
      const compareTotal = regularUnitPrice * this.requiredCount;
      const subscriptionDiscountTotal = subscriptionDiscountPerPack * this.requiredCount;
      const freePackDiscount = regularUnitPrice * Math.max(this.freeCount || 0, 0);
      const finalTotal = Math.max(compareTotal - subscriptionDiscountTotal - freePackDiscount, 0);
      const savings = Math.max(compareTotal - finalTotal, 0);

      return { compareTotal, finalTotal, savings };
    }

    const { unitPrice, comparePrice } = this.getDealUnit(purchaseType);
    const compareTotal = Math.max(comparePrice || unitPrice || 0, 0) * this.requiredCount;
    const finalTotal = Math.max(unitPrice || 0, 0) * Math.max(this.paidCount || 1, 1);
    const savings = Math.max(compareTotal - finalTotal, 0);

    return { compareTotal, finalTotal, savings };
  }

  formatCompactMoney(cents) {
    const formatted = this.formatMoney(cents || 0);
    return formatted.replace(/([.,]00)(?!\d)/, '');
  }

  pushAnalyticsEvent(eventName, payload = {}) {
    if (!eventName || typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  }

  getTierName() {
    const offer = this.offerButtons.find((button) => Number(button.dataset.requiredCount || 1) === this.requiredCount);
    const label = offer?.querySelector('span')?.textContent.trim();
    if (label) return label;
    if (this.requiredCount === 3) return 'Buy 2 get 1 Free';
    if (this.requiredCount === 6) return 'Buy 4 get 2 Free';
    return 'Single pack';
  }

  getAnalyticsUnits() {
    if (this.requiredCount === 1) return [this.getCurrentVariantUnit()];
    return this.getBundleUnits();
  }

  getPatchHandles(units = this.getAnalyticsUnits()) {
    return units
      .reduce((handles, unit) => {
        const handle = unit?.handle || unit?.id || '';
        if (handle && !handles.includes(handle)) handles.push(handle);
        return handles;
      }, [])
      .join(',');
  }

  getPatchQuantities(units = this.getAnalyticsUnits()) {
    const quantities = units.reduce((items, unit) => {
      const handle = unit?.handle || unit?.id || '';
      if (!handle) return items;
      items[handle] = (items[handle] || 0) + 1;
      return items;
    }, {});

    return Object.entries(quantities)
      .map(([handle, quantity]) => `${handle}:${quantity}`)
      .join(',');
  }

  getBundleAnalyticsPayload(extra = {}) {
    const units = this.getAnalyticsUnits();
    const totals = this.calculateTotals();

    return {
      product_handle: this.dataset.currentHandle || '',
      tier_name: this.getTierName(),
      required_count: this.requiredCount,
      paid_count: this.paidCount,
      free_count: this.freeCount,
      selected_count: this.requiredCount === 1 ? 1 : this.getBundleUnits().length,
      purchase_type: this.purchaseType,
      patch_handles: this.getPatchHandles(units),
      patch_quantities: this.getPatchQuantities(units),
      bundle_value: Number(((totals.finalTotal || 0) / 100).toFixed(2)),
      ...extra,
    };
  }

  escapeHTML(value) {
    const escapeMap = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };

    return String(value || '').replace(/[&<>"']/g, (character) => escapeMap[character]);
  }

  renderPatchDot(className, unit) {
    const color = this.escapeHTML(unit?.color || '#8A8AC0');
    const image = unit?.image ? `<img src="${this.escapeHTML(unit.image)}" alt="" loading="lazy">` : '';
    const imageClass = image ? ' has-image' : '';

    return `<span class="${className}${imageClass}" style="--patch-color: ${color}">${image}</span>`;
  }

  getBundleUnits() {
    if (this.slots.length) return this.slots.map((slot) => slot.unit).filter(Boolean);
    return this.units;
  }

  syncUnitsFromSlots() {
    this.units = this.getBundleUnits();
    if (this.slots.length) this.bundleMemoryUnits = this.units.slice();
  }

  rebuildSlots(preservedUnits = this.getBundleUnits(), seedCurrentProduct = false) {
    const rememberedUnits = Array.isArray(preservedUnits) ? preservedUnits.filter(Boolean) : [];

    if (this.requiredCount === 1) {
      if (rememberedUnits.length) this.bundleMemoryUnits = rememberedUnits.slice();
      this.slots = [];
      this.units = [];
      this.activeSlotIndex = null;
      this.closePicker();
      return;
    }

    const sourceUnits = rememberedUnits.length
      ? rememberedUnits
      : this.bundleMemoryUnits.length
        ? this.bundleMemoryUnits
        : seedCurrentProduct
          ? [this.getCurrentVariantUnit()]
          : [];
    const units = sourceUnits.slice(0, this.requiredCount);

    this.slots = Array.from({ length: this.requiredCount }, (_, index) => ({
      unit: units[index] || null,
      isFree: index >= this.paidCount,
    }));
    this.syncUnitsFromSlots();
  }

  getFirstEmptySlotIndex() {
    return this.slots.findIndex((slot) => !slot.unit);
  }

  isFreeSlotLocked(index) {
    const slot = this.slots[index];
    if (!slot?.isFree) return false;

    return this.slots.slice(0, this.paidCount).some((paidSlot) => !paidSlot.unit);
  }

  addUnitToSlot(unit) {
    if (!this.slots.length) this.rebuildSlots();
    const index = this.slots.findIndex((slot, slotIndex) => !slot.unit && !this.isFreeSlotLocked(slotIndex));
    if (index === -1) {
      this.showError(`This offer includes ${this.requiredCount} patches. Remove one to change your bundle.`);
      return false;
    }

    this.slots[index].unit = unit;
    this.syncUnitsFromSlots();
    return true;
  }

  removeUnitFromSlot(variantId) {
    for (let index = this.slots.length - 1; index >= 0; index -= 1) {
      if (this.slots[index].unit?.id === variantId) {
        this.slots[index].unit = null;
        this.syncUnitsFromSlots();
        return true;
      }
    }

    return false;
  }

  handleOfferClick(event) {
    const button = event.currentTarget;
    const preservedUnits = this.requiredCount > 1 ? this.getBundleUnits() : this.bundleMemoryUnits.slice();

    if (this.requiredCount > 1) this.bundleMemoryUnits = preservedUnits.slice();

    this.requiredCount = Number(button.dataset.requiredCount || 1);
    this.freeCount = Number(button.dataset.freeCount || 0);
    this.paidCount = Number(button.dataset.paidCount || Math.max(this.requiredCount - this.freeCount, 1));
    this.rebuildSlots(preservedUnits, this.requiredCount > 1 && !preservedUnits.length);
    this.render();
    this.pushAnalyticsEvent('duori_bundle_tier_selected', this.getBundleAnalyticsPayload());
  }

  handlePurchaseClick(event) {
    this.purchaseType = event.currentTarget.dataset.purchaseType || 'one_time';
    this.render();
    this.pushAnalyticsEvent('duori_bundle_purchase_type_selected', this.getBundleAnalyticsPayload());
  }

  handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant || !variant.id) return;

    this.dataset.currentVariantId = variant.id;
    this.dataset.currentPrice = variant.price || 0;
    this.dataset.currentComparePrice = variant.compare_at_price || variant.price || 0;
    this.dataset.currentAvailable = variant.available ? 'true' : 'false';

    const firstSellingPlan = Array.isArray(variant.selling_plan_allocations)
      ? variant.selling_plan_allocations[0]
      : null;
    this.dataset.currentSellingPlan = firstSellingPlan?.selling_plan_id || firstSellingPlan?.selling_plan?.id || '';
    this.dataset.currentSubPrice = firstSellingPlan?.price || 0;
    this.dataset.currentSubComparePrice = firstSellingPlan?.compare_at_price || variant.price || 0;

    const variantImage =
      variant.featured_image?.src ||
      variant.featured_image?.url ||
      variant.featured_media?.preview_image?.src ||
      '';
    if (variantImage) this.dataset.currentImage = variantImage;

    if (this.requiredCount > 1 && this.slots[0]) {
      this.slots[0].unit = this.getCurrentVariantUnit();
      this.syncUnitsFromSlots();
    }

    this.render();
  }

  handleCardClick(event) {
    const card = event.currentTarget;
    const data = this.getCardData(card);
    if (!data.available || !data.id) return;

    const decrement = event.target.closest('[data-bundle-decrement]');
    const increment = event.target.closest('[data-bundle-increment]');
    const currentQuantity = this.getBundleUnits().filter((unit) => unit.id === data.id).length;

    if (this.requiredCount === 1) return;

    if (decrement) {
      if (currentQuantity > 0 && this.removeUnitFromSlot(data.id)) {
        this.pushAnalyticsEvent('duori_bundle_patch_removed', this.getBundleAnalyticsPayload());
      }
      this.render();
      return;
    }

    if (increment || currentQuantity === 0) {
      if (this.addUnitToSlot(data)) {
        this.pushAnalyticsEvent('duori_bundle_patch_selected', this.getBundleAnalyticsPayload());
      }
    }

    this.render();
  }

  handleSlotClick(event) {
    const removeButton = event.target.closest('[data-bundle-slot-remove]');
    if (removeButton) {
      const index = Number(removeButton.dataset.bundleSlotRemove);
      if (!Number.isInteger(index) || !this.slots[index]) return;

      const removedUnit = this.slots[index].unit;
      this.slots[index].unit = null;
      this.syncUnitsFromSlots();
      if (this.activeSlotIndex === index) this.closePicker();
      this.render();
      if (removedUnit) this.pushAnalyticsEvent('duori_bundle_patch_removed', this.getBundleAnalyticsPayload());
      return;
    }

    const slotButton = event.target.closest('[data-bundle-slot]');
    if (!slotButton) return;

    const index = Number(slotButton.dataset.bundleSlot);
    if (!Number.isInteger(index) || !this.slots[index]) return;
    if (this.isFreeSlotLocked(index)) return;

    this.openPicker(index);
  }

  handleSlotKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.target.closest('[data-bundle-slot-remove]')) return;

    const slot = event.target.closest('[data-bundle-slot]');
    if (!slot) return;

    const index = Number(slot.dataset.bundleSlot);
    if (!Number.isInteger(index) || this.isFreeSlotLocked(index)) return;

    event.preventDefault();
    slot.click();
  }

  handlePickerClick(event) {
    const item = event.target.closest('[data-bundle-picker-item]');
    if (!item || this.activeSlotIndex === null || !this.slots[this.activeSlotIndex]) return;

    const card = this.cards.find((bundleCard) => String(bundleCard.dataset.variantId || '') === String(item.dataset.variantId || ''));
    if (!card) return;

    const data = this.getCardData(card);
    if (!data.available || !data.id) return;

    this.slots[this.activeSlotIndex].unit = data;
    this.syncUnitsFromSlots();
    this.pushAnalyticsEvent('duori_bundle_patch_selected', this.getBundleAnalyticsPayload());

    const nextSlotIndex = this.getNextEmptySlotIndex(this.activeSlotIndex + 1);
    if (nextSlotIndex >= 0) {
      this.activeSlotIndex = nextSlotIndex;
      this.updatePickerTitle(nextSlotIndex);
      if (this.pickerElement) this.pickerElement.hidden = false;
    } else {
      this.closePicker();
    }

    this.render();
  }

  handlePickerClose() {
    this.closePicker();
  }

  openPicker(index) {
    this.activeSlotIndex = index;
    this.updatePickerTitle(index);
    if (this.pickerElement) this.pickerElement.hidden = false;
    this.renderSlots();
  }

  updatePickerTitle(index) {
    if (!this.pickerTitleElement) return;
    this.pickerTitleElement.textContent = this.slots[index]?.isFree ? 'Choose your free patch' : 'Choose a patch';
  }

  getNextEmptySlotIndex(startIndex = 0) {
    if (!this.slots.length) return -1;

    for (let index = startIndex; index < this.slots.length; index += 1) {
      if (!this.slots[index].unit && !this.isFreeSlotLocked(index)) return index;
    }

    for (let index = 0; index < Math.min(startIndex, this.slots.length); index += 1) {
      if (!this.slots[index].unit && !this.isFreeSlotLocked(index)) return index;
    }

    return -1;
  }

  closePicker() {
    this.activeSlotIndex = null;
    if (this.pickerElement) this.pickerElement.hidden = true;
  }

  getSelectedUnitsForCart() {
    if (this.requiredCount === 1) {
      return [this.getCurrentVariantUnit()];
    }

    return this.getBundleUnits();
  }

  aggregateItems() {
    return this.getSelectedUnitsForCart().reduce((items, unit) => {
      const sellingPlanId = this.purchaseType === 'subscribe' ? unit.sellingPlanId : '';
      const key = `${unit.id}_${sellingPlanId || 'one_time'}`;

      if (!items[key]) {
        items[key] = {
          id: Number(unit.id),
          quantity: 0,
        };
        if (sellingPlanId) items[key].selling_plan = Number(sellingPlanId);
      }

      items[key].quantity += 1;
      return items;
    }, {});
  }

  validate() {
    const selectedCount = this.getBundleUnits().length;
    const canCheckoutAsSingle = this.requiredCount === 3 && selectedCount === 1;

    if (this.requiredCount > 1 && !canCheckoutAsSingle && selectedCount < this.requiredCount) {
      return `Fill all ${this.requiredCount} bundle slots to complete this offer.`;
    }

    const units = this.getSelectedUnitsForCart();
    const unavailableUnit = units.find((unit) => !unit.available);
    if (unavailableUnit) {
      return 'This selection is currently unavailable.';
    }

    if (this.purchaseType === 'subscribe') {
      const missingSellingPlan = units.find((unit) => !unit.sellingPlanId);
      if (missingSellingPlan) return `${missingSellingPlan.title} is not available for subscription.`;
    }

    return '';
  }

  calculateTotals(purchaseType = this.purchaseType) {
    const units = this.getSelectedUnitsForCart();
    const compareTotal = units.reduce((sum, unit) => {
      if (purchaseType === 'subscribe' && unit.subComparePrice) return sum + unit.subComparePrice;
      return sum + (unit.comparePrice || unit.price);
    }, 0);
    const sellingTotal = units.reduce((sum, unit) => {
      if (purchaseType === 'subscribe' && unit.subPrice) return sum + unit.subPrice;
      return sum + unit.price;
    }, 0);
    const freeItemValue =
      this.requiredCount > 1 && this.freeCount > 0 && units.length >= this.requiredCount
        ? [...units]
            .sort((a, b) => {
              const aPrice = purchaseType === 'subscribe' && a.subPrice ? a.subPrice : a.price;
              const bPrice = purchaseType === 'subscribe' && b.subPrice ? b.subPrice : b.price;
              return aPrice - bPrice;
            })
            .slice(0, this.freeCount)
            .reduce((sum, unit) => {
              if (purchaseType === 'subscribe' && unit.subPrice) return sum + unit.subPrice;
              return sum + unit.price;
            }, 0)
        : 0;
    const finalTotal = Math.max(sellingTotal - freeItemValue, 0);
    const savings = Math.max(compareTotal - finalTotal, 0);

    return { compareTotal, finalTotal, savings };
  }

  async handleSubmit(event) {
    event.preventDefault();
    const validationMessage = this.validate();

    if (validationMessage) {
      if (this.requiredCount > 1 && this.getBundleUnits().length < this.requiredCount) {
        this.pushAnalyticsEvent('duori_bundle_cta_incomplete', this.getBundleAnalyticsPayload());
      }
      this.showError(validationMessage);
      return;
    }

    const items = Object.values(this.aggregateItems());
    if (!items.length) return;

    this.showError('');
    this.setLoading(true);

    try {
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });
      const route = window.FoxTheme?.routes?.cart_add_url || '/cart/add.js';
      const fetchConfig = window.FoxTheme?.utils?.fetchConfig
        ? FoxTheme.utils.fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      const response = await fetch(route, { ...fetchConfig, body });
      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || 'Unable to add this bundle to cart.');
      }

      const canPublishCartUpdate =
        window.FoxTheme?.routes?.cart_url &&
        window.FoxTheme?.utils?.fetchConfig &&
        window.FoxTheme?.pubsub &&
        hasRenderableSections(parsedState.sections, sectionsToBundle);

      if (canPublishCartUpdate) {
        const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson.sections = parsedState.sections;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      } else if (this.cartDrawerElement) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      }

      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));
      if (this.requiredCount > 1) {
        this.pushAnalyticsEvent('duori_bundle_added_to_cart', this.getBundleAnalyticsPayload());
      }

      if (this.cartDrawerElement && typeof this.cartDrawerElement.show === 'function' && canPublishCartUpdate) {
        this.cartDrawerElement.show(this.submitButton);
      } else if (!this.cartDrawerElement) {
        window.location.href = window.FoxTheme?.routes?.cart_url || '/cart';
      }
    } catch (error) {
      this.showError(error.message || 'Unable to add this bundle to cart.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    const canSubmit = this.canSubmit();

    this.submitButton.classList.toggle('btn--loading', isLoading);
    this.submitButton.classList.toggle('is-complete', canSubmit);
    this.submitButton.disabled = isLoading || !canSubmit;
    this.submitButton.setAttribute('aria-disabled', isLoading || !canSubmit ? 'true' : 'false');
  }

  canSubmit() {
    if (this.requiredCount === 1) return this.getCurrentVariantUnit().available;

    const selectedUnits = this.getBundleUnits();
    const hasValidSelectionCount =
      (this.requiredCount === 3 && selectedUnits.length === 1) || selectedUnits.length >= this.requiredCount;

    return hasValidSelectionCount && selectedUnits.every((unit) => unit.available);
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.hidden = !message;
    this.errorElement.textContent = message || '';
  }

  renderOffers() {
    this.classList.toggle('is-single-offer', this.requiredCount === 1);

    this.offerButtons.forEach((button) => {
      const requiredCount = Number(button.dataset.requiredCount || 1);
      const isActive = requiredCount === this.requiredCount;
      const small = button.querySelector('small');

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');

      if (!small) return;
      if (requiredCount === 1) return;

      if (!button.dataset.offerSubtext) button.dataset.offerSubtext = small.textContent.trim();
      small.textContent = button.dataset.offerSubtext;
    });
  }

  renderPurchaseType() {
    this.purchaseButtons.forEach((button) => {
      const isActive = button.dataset.purchaseType === this.purchaseType;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.singleDailyElement) {
      this.singleDailyElement.textContent =
        this.purchaseType === 'subscribe'
          ? this.dataset.singleSubscribeDaily || 'Only $0.63/day'
          : this.dataset.singleOneTimeDaily || 'Only $0.80/day';
    }
  }

  renderCards() {
    const isSingle = this.requiredCount === 1;
    const selectedUnits = this.getBundleUnits();
    const hasEmptySlot = isSingle || this.getFirstEmptySlotIndex() !== -1;

    this.cards.forEach((card) => {
      const data = this.getCardData(card);
      const quantity = selectedUnits.filter((unit) => unit.id === data.id).length;
      const control = card.querySelector('[data-bundle-control]');
      const isUnavailable = !data.available;
      const disableCard = isSingle || isUnavailable || (!hasEmptySlot && quantity === 0);

      card.classList.toggle('is-selected', quantity > 0);
      card.classList.toggle('is-disabled', disableCard);
      card.classList.toggle('is-at-limit', !hasEmptySlot && quantity > 0);
      card.setAttribute('aria-pressed', quantity > 0 ? 'true' : 'false');
      card.disabled = disableCard;

      if (!control) return;

      if (isUnavailable) {
        control.innerHTML = '<span>Sold out</span>';
      } else if (isSingle || quantity === 0) {
        control.innerHTML = '<span>Select</span>';
      } else {
        control.innerHTML = `
          <span class="pdp-bundle-card__qty">
            <span class="pdp-bundle-card__qty-button" data-bundle-decrement aria-hidden="true">-</span>
            <span>${quantity}</span>
            <span class="pdp-bundle-card__qty-button${hasEmptySlot ? '' : ' is-disabled'}" data-bundle-increment aria-hidden="true">+</span>
          </span>
        `;
      }
    });
  }

  renderPicker() {
    if (!this.pickerGrid) return;

    this.pickerGrid.innerHTML = this.cards
      .map((card) => {
        const data = this.getCardData(card);
        const disabled = !data.available || !data.id;

        return `
          <button
            type="button"
            class="pdp-bundle-selector__picker-item${disabled ? ' is-disabled' : ''}"
            data-bundle-picker-item
            data-variant-id="${this.escapeHTML(data.id)}"
            ${disabled ? 'disabled' : ''}
          >
            ${this.renderPatchDot('pdp-bundle-selector__picker-dot', data)}
            <span>${this.escapeHTML(data.shortTitle || data.title)}</span>
          </button>
        `;
      })
      .join('');
  }

  renderSlots() {
    if (!this.slotContainer) return;

    this.slotContainer.innerHTML = this.slots
      .map((slot, index) => {
        const unit = slot.unit;
        const isLocked = this.isFreeSlotLocked(index);
        const title = unit?.shortTitle || unit?.title || (isLocked ? 'Locked' : slot.isFree ? 'Free patch' : '+ Add');
        const filledClass = unit ? ' is-filled' : '';
        const freeClass = slot.isFree ? ' is-free' : '';
        const lockedClass = isLocked ? ' is-locked' : '';
        const activeClass = this.activeSlotIndex === index ? ' is-active' : '';
        const removeLabel = unit ? `Remove ${unit.title}` : '';

        return `
          <div
            class="pdp-bundle-selector__slot${filledClass}${freeClass}${lockedClass}${activeClass}"
            role="button"
            tabindex="${isLocked ? '-1' : '0'}"
            data-bundle-slot="${index}"
            aria-disabled="${isLocked ? 'true' : 'false'}"
            aria-label="${this.escapeHTML(
              unit
                ? `Change ${unit.title}`
                : isLocked
                  ? 'Choose one more paid patch to unlock your free patch'
                  : slot.isFree
                    ? 'Add free patch'
                    : 'Add a patch'
            )}"
          >
            ${slot.isFree ? '<span class="pdp-bundle-selector__slot-label">FREE</span>' : ''}
            ${
              unit
                ? this.renderPatchDot('pdp-bundle-selector__slot-dot', unit)
                : ''
            }
            <span class="pdp-bundle-selector__slot-title">${this.escapeHTML(unit ? title : isLocked ? 'Locked' : '+ Add')}</span>
            ${
              unit
                ? `<button type="button" class="pdp-bundle-selector__slot-remove" data-bundle-slot-remove="${index}" aria-label="${this.escapeHTML(removeLabel)}">x</button>`
                : ''
            }
          </div>
        `;
      })
      .join('');
  }

  renderBuilder() {
    const isSingle = this.requiredCount === 1;
    this.classList.toggle('has-six-slots', !isSingle && this.requiredCount >= 6);
    if (this.builderElement) this.builderElement.hidden = isSingle;
    if (!this.slotContainer) return;

    if (isSingle) {
      this.slotContainer.innerHTML = '';
      return;
    }

    if (this.slots.length !== this.requiredCount) this.rebuildSlots();

    const selectedCount = this.getBundleUnits().length;
    const progress = this.requiredCount > 0 ? Math.round((selectedCount / this.requiredCount) * 100) : 0;

    if (this.progressElement) this.progressElement.style.width = `${progress}%`;
    if (this.progressLabelElement) this.progressLabelElement.textContent = `${selectedCount}/${this.requiredCount}`;
    if (this.builderHintElement) {
      if (this.requiredCount === 3 && selectedCount === 1) {
        this.builderHintElement.textContent = '1 selected — choose 1 more to unlock your free patch';
      } else if (this.requiredCount === 3 && selectedCount === 2) {
        this.builderHintElement.textContent = 'Free patch unlocked — choose your free patch';
      } else if (this.requiredCount === 3 && selectedCount >= 3) {
        this.builderHintElement.textContent = '3 patches selected — you’re getting 1 free';
      } else {
        this.builderHintElement.textContent = 'Tap any slot to choose or swap a patch';
      }
    }

    this.renderSlots();
  }

  renderFooter() {
    const selectedCount = this.requiredCount === 1 ? 1 : this.getBundleUnits().length;
    const displayTarget = this.requiredCount;
    const totals = this.calculateTotals();
    const oneTimeTotals = this.calculateTotals('one_time');
    const subscribeTotals = this.calculateTotals('subscribe');
    const savePercent = totals.compareTotal > 0 ? Math.round((totals.savings / totals.compareTotal) * 100) : 0;

    if (this.countElement) this.countElement.textContent = `${selectedCount} of ${displayTarget} Selected`;
    if (this.totalElement) this.totalElement.textContent = this.formatMoney(totals.finalTotal);
    if (this.compareElement) this.compareElement.textContent = totals.savings > 0 ? this.formatMoney(totals.compareTotal) : '';
    if (this.saveElement) this.saveElement.textContent = totals.savings > 0 ? `Save ${savePercent}%` : '';
    if (this.oneTimeTotalElement) this.oneTimeTotalElement.textContent = this.formatMoney(oneTimeTotals.finalTotal);
    if (this.oneTimeCompareElement) {
      this.oneTimeCompareElement.textContent =
        oneTimeTotals.savings > 0 ? this.formatMoney(oneTimeTotals.compareTotal) : '';
    }
    if (this.oneTimeSaveElement) this.oneTimeSaveElement.textContent = '';
    if (this.subscribeTotalElement) this.subscribeTotalElement.textContent = this.formatMoney(subscribeTotals.finalTotal);
    if (this.subscribeCompareElement) {
      this.subscribeCompareElement.textContent =
        subscribeTotals.savings > 0 ? this.formatMoney(subscribeTotals.compareTotal) : '';
    }
    if (this.subscribeSaveElement) {
      this.subscribeSaveElement.textContent =
        subscribeTotals.savings > 0 ? `Save ${this.formatCompactMoney(subscribeTotals.savings)} today` : '';
    }
    if (this.subscribeDealBadgeElement) {
      this.subscribeDealBadgeElement.hidden = false;
      this.subscribeDealBadgeElement.textContent = 'Extra 20% Off';
    }

    if (this.submitButton) {
      const canSubmit = this.canSubmit();
      const isLoading = this.submitButton.classList.contains('btn--loading');
      this.submitButton.disabled = isLoading || !canSubmit;
      this.submitButton.setAttribute('aria-disabled', isLoading || !canSubmit ? 'true' : 'false');
      this.submitButton.classList.toggle('is-complete', canSubmit);
    }

    if (this.submitText) {
      const remaining = Math.max(this.requiredCount - selectedCount, 0);
      if (this.requiredCount === 3 && selectedCount === 1) {
        this.submitText.textContent = `${this.dataset.singleLabel || 'Add to cart'} \u2014 ${this.formatCompactMoney(totals.finalTotal)}`;
      } else if (this.requiredCount === 3 && selectedCount === 2) {
        this.submitText.textContent = 'Choose your free patch to continue';
      } else if (remaining > 0) {
        this.submitText.textContent = 'Choose your patches to continue';
      } else if (this.requiredCount === 1) {
        this.submitText.textContent = `${this.dataset.singleLabel || 'Add to cart'} \u2014 ${this.formatCompactMoney(totals.finalTotal)}`;
      } else {
        this.submitText.textContent = `Add to cart \u2014 ${this.formatCompactMoney(totals.finalTotal)}`;
      }
    }
  }

  render() {
    this.renderOffers();
    this.renderPurchaseType();
    this.renderBuilder();
    this.renderCards();
    this.renderFooter();
    this.showError('');
  }

  restoreInteractiveState() {
    this.setLoading(false);
    this.render();
  }
}

customElements.define(tagName, PdpBundleSelector);
})();
